import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import {
  getPinnedTeams,
  setPinnedTeams,
  addPin,
  removePin,
  PINNED_TEAMS_CHANGED_EVENT,
} from '../../utils/enterprisePinnedTeams';
import EntHero from '../../components/enterprise/EntHero';
import EntSkeleton from '../../components/enterprise/EntSkeleton';
import EntEmptyState from '../../components/enterprise/EntEmptyState';
import EntModal from '../../components/enterprise/EntModal';
import EntToolbar from '../../components/enterprise/EntToolbar';
import { IconPlus, IconPin, IconArrowRight } from '../../components/enterprise/entIcons';
import { roleLabel } from '../../utils/companyRoles';
import {
  isValidEmail,
  normalizeEmail,
  parseEmailsFromPaste,
} from '../../utils/enterpriseUi';

export default function EnterpriseTeamsPage() {
  const { enterpriseId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('name');
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [inviteEmails, setInviteEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [creating, setCreating] = useState(false);
  const [pinnedTeams, setPinnedTeamsState] = useState([]);

  const fetchTeams = useCallback(() => {
    if (!enterpriseId) return;
    api
      .get('/api/enterprise/teams', { params: { enterpriseId } })
      .then((res) => setTeams(res.data.teams || []))
      .catch((err) => setError(err.response?.data?.error || 'Failed to load teams'));
  }, [enterpriseId]);

  useEffect(() => {
    if (!enterpriseId) return;
    setPinnedTeamsState(getPinnedTeams(enterpriseId));
  }, [enterpriseId]);

  useEffect(() => {
    const handler = () => setPinnedTeamsState(getPinnedTeams(enterpriseId));
    window.addEventListener(PINNED_TEAMS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(PINNED_TEAMS_CHANGED_EVENT, handler);
  }, [enterpriseId]);

  useEffect(() => {
    if (!enterpriseId) return undefined;
    let cancelled = false;
    setLoading(true);
    api
      .get('/api/enterprise/teams', { params: { enterpriseId } })
      .then((res) => {
        if (cancelled) return;
        const list = res.data.teams || [];
        setTeams(list);
        const teamIds = new Set(list.map((t) => t._id));
        const pinned = getPinnedTeams(enterpriseId);
        const filtered = pinned.filter((p) => teamIds.has(p.teamId));
        if (filtered.length !== pinned.length) setPinnedTeams(enterpriseId, filtered);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load teams');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enterpriseId]);

  const visibleTeams = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = teams.filter((team) => {
      if (!needle) return true;
      const managers = (team.managers || []).map((m) => m.name || '').join(' ');
      return `${team.name} ${managers}`.toLowerCase().includes(needle);
    });
    const sorted = [...filtered];
    if (sort === 'members') {
      sorted.sort((a, b) => (b.memberCount || 0) - (a.memberCount || 0) || a.name.localeCompare(b.name));
    } else {
      sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    return sorted;
  }, [query, sort, teams]);

  const handleAddEmail = () => {
    const normalized = normalizeEmail(emailInput);
    setEmailError('');
    if (!normalized) return;
    if (!isValidEmail(normalized)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    if (inviteEmails.includes(normalized)) {
      setEmailError('Already in the list');
      return;
    }
    setInviteEmails((prev) => [...prev, normalized]);
    setEmailInput('');
  };

  const handlePasteEmails = (e) => {
    const text = e.clipboardData?.getData('text') || '';
    if (!text || !/[,;\s]/.test(text)) return;
    e.preventDefault();
    const parsed = parseEmailsFromPaste(text);
    const valid = parsed.filter((em) => isValidEmail(em));
    const invalid = parsed.filter((em) => !isValidEmail(em));
    setInviteEmails((prev) => [...new Set([...prev, ...valid])]);
    setEmailError(invalid.length ? `${invalid.length} invalid email(s) skipped` : '');
    setEmailInput('');
  };

  const resetCreate = () => {
    setCreateName('');
    setInviteEmails([]);
    setEmailInput('');
    setEmailError('');
    setCreating(false);
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!createName.trim()) return;
    setCreating(true);
    const payload = { enterpriseId, name: createName.trim() };
    if (inviteEmails.length) payload.inviteEmails = inviteEmails;
    api
      .post('/api/enterprise/team/create', payload)
      .then((res) => {
        const sent = res.data.invitesSent?.length ?? 0;
        const skippedList = res.data.skipped || [];
        const sentMsg = sent ? `${sent} invite(s) sent.` : '';
        const skippedMsg = skippedList.length
          ? ` ${skippedList.length} skipped (already member or pending invite).`
          : '';
        showSuccess(`Team created. ${sentMsg}${skippedMsg}`.trim() || 'Team created.');
        resetCreate();
        setCreateOpen(false);
        fetchTeams();
      })
      .catch((err) => showError(err.response?.data?.error || 'Failed to create team'))
      .finally(() => setCreating(false));
  };

  const handleTogglePin = (e, team) => {
    e.preventDefault();
    e.stopPropagation();
    const pinned = pinnedTeams.some((p) => p.teamId === team._id);
    if (pinned) removePin(enterpriseId, team._id);
    else addPin(enterpriseId, team._id, team.name);
    setPinnedTeamsState(getPinnedTeams(enterpriseId));
  };

  const openTeam = (teamId) => navigate(`/enterprise-app/${enterpriseId}/teams/${teamId}`);

  const managerSummary = (team) => {
    const managers = team.managers || [];
    if (!managers.length) return '—';
    return managers.map((m) => `${m.name || '—'} (${roleLabel(m)})`).join(', ');
  };

  const hero = (
    <EntHero
      title="Teams"
      lead="Create groups, pin the ones you use most, and open a team to manage members."
      actions={
        <button type="button" className="ent-btn-primary" onClick={() => setCreateOpen(true)}>
          <IconPlus size={16} />
          Create team
        </button>
      }
    />
  );

  if (loading) {
    return (
      <div className="ent-content">
        {hero}
        <EntSkeleton variant="rows" count={5} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="ent-content">
        {hero}
        <div className="ent-flash ent-flash-error" role="alert">{error}</div>
      </div>
    );
  }

  return (
    <div className="ent-content">
      {hero}

      <EntToolbar
        search={query}
        onSearch={setQuery}
        searchPlaceholder="Search teams or managers"
        searchLabel="Search teams"
      >
        <select
          className="ent-select"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          aria-label="Sort teams"
        >
          <option value="name">Name A–Z</option>
          <option value="members">Most members</option>
        </select>
      </EntToolbar>

      {teams.length === 0 ? (
        <EntEmptyState
          title="No teams yet"
          lead="Create a team to organise people. You can invite members at the same time, or add them later."
          action={
            <button type="button" className="ent-btn-primary" onClick={() => setCreateOpen(true)}>
              Create team
            </button>
          }
        />
      ) : visibleTeams.length === 0 ? (
        <EntEmptyState title="No teams match that search" lead="Try a different name, or clear the search." />
      ) : (
        <>
          <div className="ent-table-wrap ent-hide-sm">
            <table className="ent-table ent-teams-table">
              <thead>
                <tr>
                  <th className="ent-teams-pin-col" aria-label="Pin" />
                  <th>Name</th>
                  <th>Managers</th>
                  <th>Members</th>
                  <th className="ent-teams-open-col" />
                </tr>
              </thead>
              <tbody>
                {visibleTeams.map((t) => {
                  const isPinned = pinnedTeams.some((p) => p.teamId === t._id);
                  return (
                    <tr
                      key={t._id}
                      className="ent-teams-row-clickable ent-row-clickable"
                      onClick={() => openTeam(t._id)}
                    >
                      <td className="ent-teams-pin-col" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className={`ent-teams-pin-btn ${isPinned ? 'ent-teams-pin-btn-pinned' : ''}`}
                          onClick={(e) => handleTogglePin(e, t)}
                          aria-label={isPinned ? 'Unpin team' : 'Pin team'}
                        >
                          <IconPin filled={isPinned} size={14} />
                        </button>
                      </td>
                      <td>{t.name}</td>
                      <td>{managerSummary(t)}</td>
                      <td>{t.memberCount ?? 0}</td>
                      <td className="ent-teams-open-col">
                        <span className="ent-teams-open-label">Open</span>
                        <span className="ent-teams-open-arrow" aria-hidden>→</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="ent-stack">
            {visibleTeams.map((t) => {
              const isPinned = pinnedTeams.some((p) => p.teamId === t._id);
              return (
                <div key={t._id} className="ent-stack-card ent-stack-card-static">
                  <div className="ent-crm-card-top">
                    <strong>{t.name}</strong>
                    <button
                      type="button"
                      className={`ent-teams-pin-btn ${isPinned ? 'ent-teams-pin-btn-pinned' : ''}`}
                      onClick={(e) => handleTogglePin(e, t)}
                      aria-label={isPinned ? 'Unpin team' : 'Pin team'}
                    >
                      <IconPin filled={isPinned} size={14} />
                    </button>
                  </div>
                  <div className="ent-stack-meta">
                    <span>{t.memberCount ?? 0} members</span>
                    <span>{managerSummary(t)}</span>
                  </div>
                  <button type="button" className="ent-btn-analytics" onClick={() => openTeam(t._id)}>
                    Open team <IconArrowRight />
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      <EntModal
        open={createOpen}
        title="Create team"
        onClose={() => {
          if (!creating) {
            setCreateOpen(false);
            resetCreate();
          }
        }}
        footer={(
          <>
            <button
              type="button"
              className="ent-btn-secondary"
              onClick={() => {
                setCreateOpen(false);
                resetCreate();
              }}
              disabled={creating}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="ent-create-team"
              className="ent-btn-primary"
              disabled={creating || !createName.trim()}
            >
              {creating ? 'Creating…' : 'Create team'}
            </button>
          </>
        )}
      >
        <form id="ent-create-team" onSubmit={handleCreate}>
          <div className="ent-form-group">
            <label htmlFor="team-name">Team name</label>
            <input
              id="team-name"
              type="text"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="e.g. Sales"
              autoComplete="off"
            />
          </div>
          <div className="ent-team-invite-optional">
            <p className="ent-team-invite-label">Invite members now (optional)</p>
            <p className="ent-team-invite-helper">
              Invites join this team as Members. Paste comma-separated emails to add several.
            </p>
            <div className="ent-team-invite-input-row">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setEmailError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddEmail();
                  }
                }}
                onPaste={handlePasteEmails}
                placeholder="colleague@company.com"
                className="ent-team-email-input"
                aria-invalid={!!emailError}
                aria-describedby={emailError ? 'team-email-error' : undefined}
              />
              <button type="button" className="ent-btn-secondary ent-btn-sm" onClick={handleAddEmail}>
                Add
              </button>
            </div>
            {emailError ? <p id="team-email-error" className="ent-team-email-error">{emailError}</p> : null}
            {inviteEmails.length > 0 ? (
              <div className="ent-team-email-chips">
                {inviteEmails.map((em) => (
                  <span key={em} className="ent-team-email-chip">
                    {em}
                    <button
                      type="button"
                      className="ent-team-email-chip-remove"
                      onClick={() => setInviteEmails((prev) => prev.filter((item) => item !== em))}
                      aria-label={`Remove ${em}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </form>
      </EntModal>
    </div>
  );
}
